import axios from 'axios';
import get from 'lodash.get';

import { isSplashApiError } from '~/domains/services/utils/isBEApiError';
import type { LocationCheckError } from '~/utils/geocomply';

const nonEmpty = (value: string | null | undefined, fallback: string): string =>
  value && value.length > 0 ? value : fallback;

const isLocationCheckErrors = (error: unknown): error is LocationCheckError[] =>
  Array.isArray(error) && error.some((item) => !!get(item, 'description', ''));

const JURISDICTION_ERROR_CODES = ['JURISDICTION_PROHIBITED'];

const DEVICE_LOCATION_ERROR_CODES = ['INVALID_LOCATION', 'LOCATION_ERROR'];

export type OrderLocationError =
  { type: 'device'; errors: LocationCheckError[] } | { type: 'jurisdiction' };

export const getOrderLocationError = (error: unknown): OrderLocationError | null => {
  if (isLocationCheckErrors(error)) return { type: 'device', errors: error };
  if (!axios.isAxiosError(error)) return null;

  const code: string = get(error, 'response.data.error', '');

  if (JURISDICTION_ERROR_CODES.includes(code)) return { type: 'jurisdiction' };
  if (DEVICE_LOCATION_ERROR_CODES.includes(code)) return { type: 'device', errors: [] };

  return null;
};

export const isCancelledError = (error: unknown): boolean => axios.isCancel(error);

export const isNoConnectionError = (error: unknown): boolean =>
  axios.isAxiosError(error) && !error.response && error.code === axios.AxiosError.ERR_NETWORK;

export const getBeErrorMessage = (error: unknown): string | null => {
  if (isLocationCheckErrors(error))
    return error.find((item) => !!item.description)?.description ?? null;
  if (isSplashApiError(error)) return error.response?.data?.message || null;
  if (axios.isAxiosError<{ message?: string }>(error)) return error.response?.data?.message || null;

  return null;
};

export const getApiErrorMessage = (error: unknown, fallback = 'An error occurred'): string => {
  if (isLocationCheckErrors(error))
    return nonEmpty(error.find((item) => !!item.description)?.description, fallback);
  if (isSplashApiError(error)) return nonEmpty(error.response?.data?.message, fallback);
  if (axios.isAxiosError<{ message?: string }>(error))
    return nonEmpty(error.response?.data?.message ?? error.message, fallback);
  if (error instanceof Error) return nonEmpty(error.message, fallback);

  return fallback;
};
